// Words for Developer › Integrations (DSEC INT; apiv2/developer.openapi.yaml tag `integrations`).
// developerContract.test.js checks that every enum value of the contract has its words here; a value a
// newer backend adds is shown as it is. The sentences (status, setup help) are built here too, so the
// cards hold no per-vendor words: every system name comes from the API.
import moment from 'moment';

import type { Label } from '../labels';
import type {
    Integration, IntegrationAccount, IntegrationCredentials, IntegrationFact, IntegrationStatus, SettingValue,
} from '../integrationTypes';

const lookup = (table: Record<string, Label>, value: string | null | undefined): Label => (
    (value != null && table[value]) || { label: value == null ? '—' : String(value), tone: 'neutral' }
);

/** A connection's verdict: never shown by colour alone (Verdict.tsx adds the icon). */
export const VERDICTS: Record<string, Label> = {
    receiving: { label: 'Receiving', tone: 'success', hint: 'A webhook was accepted in the last 48 hours.' },
    quiet: { label: 'Quiet', tone: 'warning', hint: 'No webhook was accepted in the last 48 hours.' },
    rejecting: { label: 'Rejecting', tone: 'error', hint: 'Webhooks are refused, and none was accepted in the last 24 hours.' },
    incomplete: { label: 'Incomplete', tone: 'warning', hint: 'Required credentials or settings are missing.' },
    not_connected: { label: 'Not connected', tone: 'warning', hint: 'No credentials are set, so nothing can arrive.' },
    paused: { label: 'Paused', tone: 'warning', hint: 'Webhooks are switched off: they are answered and ignored.' },
    unknown: { label: 'Not measured', tone: 'neutral', hint: 'Webhook metrics are off: only the last request is known, which does not show that bookings arrive.' },
    polling: { label: 'Polling', tone: 'success', hint: 'The last poll succeeded within 5 minutes.' },
    delayed: { label: 'Delayed', tone: 'warning', hint: 'No successful poll for over 5 minutes.' },
    failing: { label: 'Failing', tone: 'error', hint: 'No successful poll for 15 minutes or more, or 3 failed polls in a row.' },
    off: { label: 'Off', tone: 'neutral', hint: 'Switched off: nothing is polled.' },
};
export const verdict = (value: string | null | undefined) => lookup(VERDICTS, value);

/** The receiver's reasons (`WebhookReason`), as the end of a sentence ("rejected since 09:12: wrong token"). */
export const WEBHOOK_REASONS: Record<string, string> = {
    auth_failed: 'wrong token or signature',
    credential_denied: 'the API credential was refused',
    tenant_not_found: 'the company in the address is unknown',
    account_not_found: 'the account in the address is unknown',
    bad_payload: 'the body could not be read',
    too_large: 'the body was too large',
    exception: 'an error on our side',
    processing_failed: 'processing failed',
    disable_webhooks: 'all webhooks are switched off',
    disable_booking_webhooks: 'booking webhooks are switched off',
    disable_order_webhooks: 'order webhooks are switched off',
    disable_departure_webhooks: 'departure webhooks are switched off',
    disable_product_webhooks: 'product webhooks are switched off',
    disable_notifications: 'notifications are switched off',
    ignore_webhooks: 'Curated Planet switched webhooks off for your company',
    filtered: 'filtered out',
    handshake: 'a set-up check by the booking system',
};

/** The other codes `IntegrationStatus.reason` carries. */
export const STATUS_REASONS: Record<string, string> = {
    missing_keys: 'required credentials or settings are missing',
    no_credentials: 'credentials missing',
    poll_failed: 'the last polls failed',
    poll_stale: 'no recent poll',
    metrics_off: 'webhook metrics are off',
    metrics_unavailable: 'webhook metrics are unavailable right now',
    poll_unknown: 'the last fetch of bookings is not recorded yet',
};

export const reasonText = (code: string | null | undefined) => (
    code ? WEBHOOK_REASONS[code] || STATUS_REASONS[code] || code.replace(/_/g, ' ') : null
);

/** A verdict's hint where its reason changes what it means: `unknown` is "metrics off" for a webhook system but
 *  "no poll recorded yet" for a polled one (Juniper, Salesforce: `poll_unknown`). */
const REASON_HINTS: Record<string, string> = {
    poll_unknown: 'Bookings are fetched from the booking system, and the time of the last fetch is not recorded yet: its health is not measured.',
};

/** The verdict's hint, with its reason. */
export const verdictHint = (status: IntegrationStatus) => {
    if (status.reason && REASON_HINTS[status.reason]) {
        return REASON_HINTS[status.reason];
    }
    const words = verdict(status.verdict);
    const reason = reasonText(status.reason);
    return reason ? `${words.hint || words.label} (${reason})` : words.hint;
};

export const WEBHOOK_TYPES: Record<string, string> = {
    booking: 'Bookings',
    order: 'Orders',
    departure: 'Departures',
    product: 'Products',
    notification: 'Notifications',
};

export const WEBHOOK_OUTCOMES: Record<string, Label> = {
    accepted: { label: 'Accepted', tone: 'success' },
    disabled: { label: 'Ignored (switched off)', tone: 'neutral' },
    rejected_auth: { label: 'Rejected: authentication', tone: 'error' },
    invalid: { label: 'Rejected: not valid', tone: 'error' },
    error: { label: 'Failed', tone: 'error' },
};

export const WEBHOOK_EFFECTS: Record<string, string> = {
    none: 'No change',
    import: 'Import',
    inline_action: 'Direct change',
    queued: 'Queued for import',
};

export const WEBHOOK_ACTIONS: Record<string, string> = {
    cancel: 'Cancellation',
    delete: 'Deletion',
    check_in: 'Check-in',
    no_show: 'No-show',
    frozen_update: 'Update of a frozen booking',
};

export const SETUP_MODES: Record<string, string> = {
    api: 'Installed for you',
    email: "Set up by the booking system's support",
    manual: 'Set up in your booking system',
    none: 'No webhooks',
};

/** What the webhook set-up asks of the client, by `setup_mode`. */
export const setupHelp = (mode: string, name: string, environment: string) => {
    if (mode === 'api') {
        return environment === 'sandbox'
            ? `A sandbox never writes at ${name}: add these addresses in ${name} yourself.`
            : `Curated Planet installs these webhooks at ${name} for you. Install them again after replacing the credentials.`;
    }
    if (mode === 'email') {
        return `Curated Planet asks ${name}'s support to send webhooks to these addresses: nothing to set up on your side.`;
    }
    if (mode === 'manual') {
        return `Add these addresses in ${name}, in its webhook settings.`;
    }
    return null;
};

export const FACT_KINDS: Record<string, { label: string; hint: string }> = {
    webhooks_24h: { label: 'Webhooks, 24 h', hint: 'Webhooks received in the last 24 hours, accepted or not.' },
    rejected_24h: { label: 'Rejected, 24 h', hint: 'Refused: a wrong token or signature, an unknown account, a body that could not be read.' },
    changes_24h: { label: 'Changes, 24 h', hint: 'Imports and direct changes (cancellations, check-ins …) that webhooks caused.' },
    last_import: { label: 'Last import', hint: 'The last import of this booking system.' },
    last_poll: { label: 'Last poll', hint: 'The last successful poll.' },
    bookings_24h: { label: 'Bookings, 24 h', hint: 'Bookings imported in the last 24 hours.' },
    last_request: { label: 'Last request', hint: 'The last request received, accepted or not. With the metrics off nothing more is known.' },
    vehicles: { label: 'Vehicles', hint: 'Vehicles in the last poll.' },
    mapped_products: { label: 'Mapped products', hint: 'Products of the booking system linked to your trips.' },
};
export const factKind = (kind: string) => FACT_KINDS[kind] || { label: kind.replace(/_/g, ' '), hint: '' };

export const IMPORT_STATES: Record<string, Label> = {
    success: { label: 'succeeded', tone: 'success' },
    partial: { label: 'partly imported', tone: 'warning' },
    failure: { label: 'failed', tone: 'error' },
    rejected: { label: 'rejected', tone: 'error' },
};
export const importState = (value: string | null | undefined) => lookup(IMPORT_STATES, value);

export const METRICS_NOTES: Record<string, string> = {
    metrics_off: 'Detailed health needs the webhook metrics, which are off: only the last request is known.',
    metrics_unavailable: 'The webhook metrics cannot be read right now: only the last request is shown.',
};

/** Where a setting's value comes from (`SettingSource`), on the connection. */
export const SETTING_SOURCES: Record<string, string> = {
    connection: 'Changed',
    account: 'Set for this account',
    default: 'Default',
    parent: 'From the franchise',
};

export const PROBE_RESULTS: Record<string, Label> = {
    ok: { label: 'Accepted', tone: 'success' },
    rejected: { label: 'Refused', tone: 'error' },
    unreachable: { label: 'Could not be checked', tone: 'warning' },
};

export const CONNECT_MODES: Record<string, string> = {
    credentials: 'Connect with its API credentials',
    easy: 'Connect with its API credentials',
    support: 'Curated Planet support connects it for you',
};

export const VENDOR_CLEANUP: Record<string, string> = {
    uninstall: 'Uninstalled at the booking system first',
    manual: 'Remove the webhooks at the booking system yourself',
    none: 'Nothing to remove at the vendor',
};

export const INSTALL_REFUSALS: Record<string, string> = {
    sandbox: 'A sandbox never writes at the booking system.',
    not_api: 'Curated Planet does not install webhooks for this booking system.',
};

export const FLEET_SYNC_TYPES: Record<string, string> = {
    pull: 'Polled',
    push: 'Sent by the fleet system (not polled)',
};

export const INTEGRATION_KINDS: Record<string, string> = {
    booking: 'Booking system',
    fleet: 'Fleet tracking',
};

export const MANAGED_BY: Record<string, string> = {
    client: 'You',
    curated_planet: 'Curated Planet',
};

/** Who replaces an account's credentials (`IntegrationCredentials.managed_by`). */
export const CREDENTIALS_MANAGED_BY: Record<string, string> = {
    client: 'You',
    curated_planet: 'Managed by Curated Planet: ask support to change them.',
    vendor_app: "Connected through the booking system's app: change them there.",
    accounts: 'Each named account holds its own credentials: this one keeps the shared settings.',
};

/** The account runs on credentials of its own, all of them in effect: what Check and Install use (the backend's
 *  one answer, `credentials`). An empty group -- a Default whose named accounts hold the keys, a system without
 *  vendor credentials -- has nothing to check and is never "set". */
export const credentialsReady = (credentials: IntegrationCredentials | null | undefined) => (
    Boolean(credentials && credentials.complete && credentials.keys.length > 0)
);

/** The row's credential mark: set / missing, or why there is nothing to set. */
export const credentialState = (credentials: IntegrationCredentials) => {
    if (credentials.keys.length === 0) {
        return { ok: true, text: credentials.managed_by === 'accounts' ? 'Credentials on accounts' : 'No credentials' };
    }
    return credentials.complete ? { ok: true, text: 'Credentials set' } : { ok: false, text: 'Credentials missing' };
};

/** A removal answered 200 (`IntegrationRemoval.vendor_webhooks`). */
export const REMOVAL_VENDOR_WEBHOOKS: Record<string, string> = {
    remove_at_vendor: 'The booking system refused the key, so its webhooks could not be removed from here: remove them there.',
};

// ---- formatting --------------------------------------------------------------------------------------

/** "4 minutes ago" (the exact time is in `formatTs`). */
export const ago = (at: string | null | undefined) => (at ? moment(at).fromNow() : null);

/** A day for "none since …" (`tracked_since`): 22 Oct, or 22 Oct 2025 in another year. */
export const sinceDay = (at: string) => {
    const m = moment(at);
    return m.isSame(moment(), 'year') ? m.format('D MMM') : m.format('D MMM YYYY');
};

/** I2's pseudo-account and pseudo-system of a webhook whose URL named one we do not know: never a row or a card. */
export const UNKNOWN_SLUG = '_unknown';

/** A clock time for "since …": today 09:12, this year 22 Oct, 09:12, else 22 Oct 2025. */
export const clockTime = (at: string) => {
    const m = moment(at);
    if (m.isSame(moment(), 'day')) {
        return m.format('HH:mm');
    }
    return m.isSame(moment(), 'year') ? m.format('D MMM, HH:mm') : m.format('D MMM YYYY');
};

const ACRONYMS: Record<string, string> = { id: 'ID', ids: 'IDs', url: 'URL', api: 'API', pax: 'pax' };

/** A schema key in words, when its schema has no `title`: `custom_field_guide_id` → "Custom field guide ID". */
export const humanizeKey = (key: string) => {
    const words = key.split('_').filter(Boolean).map((w) => ACRONYMS[w] || w);
    const text = words.join(' ');
    return text ? text[0].toUpperCase() + text.slice(1) : key;
};

/** A setting's value in words (never raw JSON): enum labels, On/Off, lists. */
export const settingText = (value: any, prop: Record<string, any> | undefined): string => {
    if (value === null || value === undefined || value === '') {
        return 'not set';
    }
    if (prop?.enum && Array.isArray(prop['x-enum-labels'])) {
        const idx = prop.enum.indexOf(value);
        if (idx >= 0 && prop['x-enum-labels'][idx]) {
            return prop['x-enum-labels'][idx];
        }
    }
    if (typeof value === 'boolean') {
        return value ? 'On' : 'Off';
    }
    if (Array.isArray(value)) {
        return value.length ? value.map((v) => settingText(v, prop?.items)).join(', ') : 'none';
    }
    if (typeof value === 'object') {
        return Object.entries(value).map(([k, v]) => `${prop?.properties?.[k]?.title || humanizeKey(k)}: ${settingText(v, prop?.properties?.[k])}`).join('; ');
    }
    const unit = prop?.['x-unit'];
    return unit ? `${value} ${unit}` : String(value);
};

/** On an account: where its value comes from, in words; an override names the connection's value it replaces. */
export const inheritText = (stored: SettingValue, prop: Record<string, any> | undefined, connection?: SettingValue) => {
    if (stored.overridden || stored.source === 'account') {
        return connection ? `Overridden (connection: ${settingText(connection.value, prop)})` : 'Overridden for this account';
    }
    const text = settingText(stored.value, prop);
    return stored.source === 'default' ? `Inherits the default: ${text}` : `Inherits ${text} from the connection`;
};

// ---- the status sentence ------------------------------------------------------------------------------

/** The card's status sentence (plan §1.2): what the verdict is about, with the instant and the reason, in the
 *  words of its kind -- vehicle positions for fleet, bookings for a booking system (a polled one included). */
export const statusSentence = (status: IntegrationStatus, connection: Pick<Integration, 'kind' | 'fleet'>, trackedSince: string | null = null) => {
    const at = status.at;
    const reason = reasonText(status.reason);
    const fleet = connection.kind === 'fleet';
    // A polled booking system (Juniper, Salesforce) answers `unknown` / `poll_unknown` until its last poll is
    // recorded; the poll verdicts (polling / delayed / failing / off) are worded for bookings for when it is.
    switch (status.verdict) {
        case 'receiving':
            return `Bookings arrive by webhook · last accepted ${ago(at) || 'recently'}`;
        case 'quiet':
            if (at) {
                return `No webhook accepted since ${clockTime(at)}`;
            }
            return trackedSince ? `No webhook accepted since ${sinceDay(trackedSince)}` : 'No webhook accepted yet';
        case 'rejecting':
            return `Requests rejected${at ? ` since ${clockTime(at)}` : ''}${reason ? `: ${reason}` : ''}`;
        case 'incomplete':
            return `Not ready: ${reason || 'required credentials or settings are missing'}`;
        case 'not_connected':
            return 'Not connected: credentials missing';
        case 'paused':
            return `Paused: ${reason || 'webhooks are switched off'}. Webhooks are answered and ignored.`;
        case 'unknown':
            if (status.reason === 'poll_unknown') {
                return 'Bookings are fetched from the booking system · the last fetch is not recorded yet';
            }
            return at ? `Last request ${ago(at)} · detailed health needs metrics` : 'No request yet · detailed health needs metrics';
        case 'polling':
            return fleet
                ? `Vehicle positions are polled · last poll ${ago(at) || 'recently'}`
                : `Bookings are fetched from the booking system · last fetch ${ago(at) || 'recently'}`;
        case 'delayed':
            if (!at) {
                return fleet ? 'Delayed: no successful poll yet' : 'Delayed: bookings not fetched yet';
            }
            return fleet ? `Delayed: last successful poll ${ago(at)}` : `Delayed: bookings last fetched ${ago(at)}`;
        case 'failing': {
            const error = connection.fleet?.last_error;
            const what = fleet ? 'Polling fails' : 'Fetching bookings fails';
            return `${what}${at ? ` · last success ${ago(at)}` : ''}${error ? ` · ${error}` : ''}`;
        }
        case 'off':
            return fleet ? 'Switched off: vehicle positions are not polled' : 'Switched off: bookings are not fetched';
        default:
            return reason || verdict(status.verdict).label;
    }
};

/** An account row's "last accepted" (or what is known instead). */
export const accountActivity = (account: IntegrationAccount, trackedSince: string | null = null) => {
    if (account.last_accepted_at) {
        return `last accepted ${ago(account.last_accepted_at)}`;
    }
    const requests = Object.values(account.webhooks?.last_request_at || {}).filter(Boolean) as string[];
    const last = requests.sort().pop();
    if (account.status.verdict === 'unknown') {
        return last ? `last request ${ago(last)}` : (trackedSince ? `no request since ${sinceDay(trackedSince)}` : 'no request yet');
    }
    if (account.last_rejected_at) {
        return `last rejected ${ago(account.last_rejected_at)}`;
    }
    if (!account.webhooks) {
        return null;
    }
    return trackedSince ? `nothing accepted since ${sinceDay(trackedSince)}` : 'nothing accepted yet';
};

/** A fact tile's value in words. */
export const factValue = (fact: IntegrationFact, trackedSince: string | null = null): string => {
    if (['last_import', 'last_poll', 'last_request'].includes(fact.kind)) {
        if (fact.at) {
            return ago(fact.at) as string;
        }
        // The backend cannot know it now (health.FactUnknown, e.g. the last import before its index exists): the
        // note says so. (A last request's note is about the metrics; its value is Redis's, known.)
        if (fact.note && fact.kind !== 'last_request') {
            return 'Not known';
        }
        // Older history was not recorded (I2 review L5): "none since", never a "never" it cannot know.
        return trackedSince ? `None since ${sinceDay(trackedSince)}` : 'Never';
    }
    if (fact.value === null || fact.value === undefined) {
        return '—';
    }
    const n = fact.value.toLocaleString();
    return fact.kind === 'mapped_products' && fact.total != null ? `${n} of ${fact.total.toLocaleString()}` : n;
};
